// frontend/src/pages/Shop.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../context/TenantContext";
import { useCart } from "../context/CartContext";
import ProductGrid from "../components/Product/ProductGrid";
import BookingGrid from "../components/Booking/BookingGrid";
import StorefrontInlineBanner from "../components/Layout/StorefrontInlineBanner";
import StorefrontCategoryIntro from "../components/Layout/StorefrontCategoryIntro";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import { sanitizeImageUrl } from "../utils/imageUtils";
import { getStorefrontThemeConfig } from "../utils/storefrontTheme";
import { getConfiguredStorefrontBanners } from "../utils/storefrontBanners";
import api from "../services/api";
import toast from "react-hot-toast";
import {
  FiFilter,
  FiGrid,
  FiList,
  FiChevronDown,
  FiChevronRight,
  FiX,
  FiSearch,
  FiTag,
  FiStar,
  FiTrendingUp,
  FiClock,
  FiDollarSign,
  FiMenu,
  FiPackage,
  FiServer,
  FiLayers,
  FiTruck,
  FiMapPin,
  FiCheck,
  FiRefreshCw,
  FiSliders,
  FiBox,
  FiDroplet,
  FiMaximize,
  FiCalendar,
  FiShoppingBag,
  FiHeart,
  FiFolder,
  FiCheckCircle,
} from "react-icons/fi";
import {
  FaTshirt,
  FaShoePrints,
  FaWeightHanging,
  FaPiggyBank,
  FaPalette,
  FaRulerCombined,
  FaTags,
} from "react-icons/fa";
import { MdOutlineStraighten } from "react-icons/md";
import "../styles/shop.css";
import "../styles/booking.css";

// SVG Placeholder for missing/invalid images
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300' viewBox='0 0 300 300'%3E%3Crect width='300' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='45%25' font-family='Arial' font-size='48' fill='%23ddd' text-anchor='middle' dy='.3em'%3E%F0%9F%93%A6%3C/text%3E%3Ctext x='50%25' y='55%25' font-family='Arial' font-size='14' fill='%23999' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

// ============================================
// PRODUCT TYPE CONFIGURATION
// ============================================
const PRODUCT_TYPES = [
  {
    value: "physical",
    label: "Physical Products",
    icon: FiPackage,
    color: "#2c7da0",
  },
  {
    value: "livestock",
    label: "Livestock & Animals",
    icon: FaPiggyBank,
    color: "#8d6e63",
  },
  {
    value: "digital",
    label: "Digital Products",
    icon: FiServer,
    color: "#1565c0",
  },
  {
    value: "service",
    label: "Services",
    icon: FiLayers,
    color: "#455a64",
  },
];

const CatalogModeTabs = ({
  catalogMode,
  onChange,
  productsCount,
  bookingsCount,
  bookingToggle,
  primaryColor,
}) => (
  <div
    className={`shop-catalog-mode shop-catalog-mode--${bookingToggle.shopStyle} shop-catalog-mode--size-${bookingToggle.shopSize}`}
    role="tablist"
    aria-label="Browse catalog"
    style={{ "--shop-toggle-accent": primaryColor || "#2c7da0" }}
  >
    <button
      type="button"
      role="tab"
      aria-selected={catalogMode === "products"}
      className={catalogMode === "products" ? "active" : ""}
      onClick={() => onChange("products")}
    >
      <FiPackage />
      <span>{bookingToggle.productLabel}</span>
      {bookingToggle.showCounts && <small>{productsCount}</small>}
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={catalogMode === "bookings"}
      className={catalogMode === "bookings" ? "active" : ""}
      onClick={() => onChange("bookings")}
    >
      <FiCalendar />
      <span>{bookingToggle.bookingLabel}</span>
      {bookingToggle.showCounts && <small>{bookingsCount}</small>}
    </button>
  </div>
);

const getProductType = (product) =>
  String(product.ProductType || product.Metadata?.productType || "")
    .trim()
    .toLowerCase();

// ============================================
// MEASUREMENT TYPE CONFIGURATION
// ============================================
const MEASUREMENT_TYPES = {
  size: { label: "Size", icon: FaTshirt },
  shoe_size: { label: "Shoe Size", icon: FaShoePrints },
  weight: { label: "Weight", icon: FaWeightHanging },
  volume: { label: "Volume", icon: FiDroplet },
  dimensions: { label: "Dimensions", icon: FiMaximize },
  length: { label: "Length", icon: MdOutlineStraighten },
  count: { label: "Count", icon: FiPackage },
  animal_weight: { label: "Animal Weight", icon: FaWeightHanging },
  animal_count: { label: "Animal Count", icon: FaPiggyBank },
  animal_age: { label: "Animal Age", icon: FiCalendar },
};

// ============================================
// SORT OPTIONS
// ============================================
const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "popular", label: "Most Popular" },
  { value: "discount", label: "Best Discount" },
  { value: "name-asc", label: "Name: A to Z" },
  { value: "name-desc", label: "Name: Z to A" },
];

// ============================================
// CONDITION OPTIONS
// ============================================
const CONDITION_OPTIONS = [
  { value: "New", label: "New" },
  { value: "Used", label: "Used" },
  { value: "Refurbished", label: "Refurbished" },
  { value: "Like New", label: "Like New" },
  { value: "For Parts", label: "For Parts" },
];

// ============================================
// SOUTH AFRICAN PROVINCES
// ============================================
const SA_PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
  "International",
];

// ============================================
// LIVESTOCK ANIMAL TYPES
// ============================================
const ANIMAL_TYPES = [
  "Cattle",
  "Sheep",
  "Goat",
  "Pig",
  "Poultry",
  "Rabbit",
  "Horse",
  "Donkey",
  "Camel",
  "Other",
];

// ============================================
// FILTER SECTION COMPONENT
// ============================================
const FilterSection = ({
  title,
  icon: Icon,
  children,
  defaultOpen = true,
  count = null,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="filter-section">
      <button
        type="button"
        className="filter-section-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="filter-section-title">
          {Icon && <Icon size={16} />}
          {title}
          {count !== null && count > 0 && (
            <span className="filter-count-badge">{count}</span>
          )}
        </span>
        <FiChevronDown
          className={`filter-section-arrow ${isOpen ? "open" : ""}`}
        />
      </button>
      {isOpen && <div className="filter-section-content">{children}</div>}
    </div>
  );
};

// ============================================
// CATEGORY TREE COMPONENT
// ============================================
const CategoryTree = ({ categories, selectedCategories, onSelect }) => {
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpand = (categoryId, e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedIds(newExpanded);
  };

  const buildTree = (cats, parentId = null) => {
    return cats
      .filter((cat) => {
        const catParentId = cat.ParentID || cat.ParentCategoryID || null;
        return catParentId === parentId;
      })
      .map((cat) => ({
        ...cat,
        children: buildTree(cats, cat.CategoryID),
      }));
  };

  const tree = buildTree(categories);

  const hasProductsInSubtree = (n) => {
    if (n.productCount > 0) return true;
    return n.children?.some((child) => hasProductsInSubtree(child));
  };

  const renderNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(node.CategoryID);
    const isSelected = selectedCategories.includes(node.CategoryID);
    const productCount = node.productCount || 0;

    if (!hasProductsInSubtree(node)) return null;

    return (
      <div key={node.CategoryID} className="category-tree-node">
        <div
          className={`category-tree-item ${isSelected ? "selected" : ""}`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => onSelect(node.CategoryID)}
        >
          {hasChildren ? (
            <button
              type="button"
              className="category-expand-btn"
              onClick={(e) => toggleExpand(node.CategoryID, e)}
            >
              <FiChevronRight
                size={14}
                className={`expand-icon ${isExpanded ? "expanded" : ""}`}
              />
            </button>
          ) : (
            <span className="category-expand-placeholder" />
          )}
          <span className="category-tree-name">{node.Name}</span>
          <span className="category-tree-count">{productCount}</span>
        </div>
        {hasChildren && isExpanded && (
          <div className="category-tree-children">
            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="category-tree">{tree.map((node) => renderNode(node))}</div>
  );
};

// ============================================
// MAIN SHOP COMPONENT
// ============================================
const Shop = () => {
  const { tenant, settings, loading: tenantLoading } = useTenant();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // ============================================
  // STATE
  // ============================================
  const [searchQuery, setSearchQuery] = useState(() => {
    return new URLSearchParams(location.search).get("search") || "";
  });
  const [sortBy, setSortBy] = useState("featured");
  const [viewMode, setViewMode] = useState("grid");
  const [showMobileFilterSheet, setShowMobileFilterSheet] = useState(false);
  const [showMobileCategorySheet, setShowMobileCategorySheet] = useState(false);

  // Sidebar filter states
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const category = new URLSearchParams(location.search).get("category");
    return category ? [category] : [];
  });
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [catalogMode, setCatalogMode] = useState("products");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 50000 });
  const [priceRangeInput, setPriceRangeInput] = useState({
    min: "",
    max: "",
  });
  const [selectedMeasurementTypes, setSelectedMeasurementTypes] = useState([]);
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAnimalTypes, setSelectedAnimalTypes] = useState([]);
  const [showInStockOnly, setShowInStockOnly] = useState(false);
  const [showOnSaleOnly, setShowOnSaleOnly] = useState(false);
  const [showNewArrivalsOnly, setShowNewArrivalsOnly] = useState(false);
  const [minRating, setMinRating] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchQuery(params.get("search") || "");
    const category = params.get("category");
    setSelectedCategories(category ? [category] : []);
  }, [location.search]);

  // Computed max price for slider
  const [maxProductPrice, setMaxProductPrice] = useState(50000);

  // ============================================
  // DATA FETCHING
  // ============================================
  const {
    data: productsData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["shop-products", tenant?.TenantID],
    async () => {
      const response = await api.get("/api/products?limit=100");
      return response.data;
    },
    {
      enabled: !!tenant,
      retry: 2,
      staleTime: 60000,
      keepPreviousData: true,
    },
  );

  const {
    data: bookingData,
    isLoading: bookingsLoading,
    error: bookingsError,
    refetch: refetchBookings,
  } = useQuery(
    ["shop-bookings", tenant?.TenantID],
    async () => {
      const response = await api.get(
        "/api/products?productType=booking&limit=100",
      );
      return response.data;
    },
    {
      enabled: !!tenant,
      retry: 2,
      staleTime: 60000,
    },
  );

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery(
    ["shop-categories", tenant?.TenantID],
    async () => {
      const response = await api.get("/api/public/categories/active");
      return response.data?.categories || [];
    },
    {
      enabled: !!tenant,
      retry: 1,
      staleTime: 300000,
    },
  );

  const { data: promotionsData } = useQuery(
    ["shop-promotions", tenant?.TenantID],
    async () => {
      try {
        const response = await api.get("/api/promotions/active");
        return response.data;
      } catch (err) {
        return { promotions: [] };
      }
    },
    {
      enabled: !!tenant && !!productsData,
      retry: 1,
      staleTime: 300000,
    },
  );

  const { data: activeSlides = [] } = useQuery(
    ["shop-active-slides", tenant?.TenantID],
    async () => {
      const response = await api.get("/api/public/slides/active");
      return Array.isArray(response.data?.slides) ? response.data.slides : [];
    },
    { enabled: !!tenant, staleTime: 300000, retry: 1 },
  );
  const storefrontContent = getStorefrontThemeConfig(
    settings?.storefrontTemplate,
    settings?.storefrontThemeConfig,
  ).storefrontContent;
  const bookingToggle = storefrontContent.bookingToggle;
  const storefrontPrimaryColor =
    settings?.storefrontThemeConfig?.primaryColor || tenant?.PrimaryColor;
  const inlineBanners = getConfiguredStorefrontBanners(
    storefrontContent.heroSlides,
    activeSlides,
    tenant?.PrimaryColor,
  ).filter(
    (slide) => slide.placement === "inline" && slide.pagePath === "/shop",
  );

  // ============================================
  // PROMOTION MAP
  // ============================================
  const productPromotionMap = useMemo(() => {
    const map = new Map();

    if (promotionsData?.promotions) {
      promotionsData.promotions.forEach((promo) => {
        const productId = promo.ProductID || promo.productId;
        if (productId) {
          map.set(productId, promo);
        }
      });
    }

    if (productsData?.products) {
      const now = new Date();
      productsData.products.forEach((product) => {
        if (product.Promotions && Array.isArray(product.Promotions)) {
          const activePromo = product.Promotions.find((p) => {
            const start = new Date(p.StartDate);
            const end = new Date(p.EndDate);
            return p.IsActive && now >= start && now <= end;
          });
          if (activePromo && !map.has(product.ProductID)) {
            map.set(product.ProductID, activePromo);
          }
        }
      });
    }

    return map;
  }, [promotionsData, productsData]);

  const getProductPromotion = (productId, categoryId) => {
    if (productPromotionMap.has(productId)) {
      return productPromotionMap.get(productId);
    }
    if (categoryId) {
      for (const [id, promo] of productPromotionMap) {
        if (promo.CategoryID === categoryId) {
          return promo;
        }
      }
    }
    return null;
  };

  // ============================================
  // ALL PRODUCTS
  // ============================================
  const allProducts = useMemo(
    () => productsData?.products || [],
    [productsData?.products],
  );
  const bookingProducts = useMemo(
    () => bookingData?.products || [],
    [bookingData?.products],
  );
  const catalogProducts = useMemo(
    () =>
      catalogMode === "bookings"
        ? bookingProducts
        : allProducts.filter((product) => getProductType(product) !== "booking"),
    [allProducts, bookingProducts, catalogMode],
  );
  const productsCount = allProducts.filter(
    (product) => getProductType(product) !== "booking",
  ).length;
  const bookingsCount = bookingProducts.length;

  // ============================================
  // COMPUTE FILTER OPTIONS FROM PRODUCTS
  // ============================================

  useEffect(() => {
    if (allProducts.length > 0) {
      const prices = allProducts
        .map((p) => p.SellingPrice || 0)
        .filter((p) => p > 0);
      const maxPrice = Math.max(...prices, 1000);
      const roundedMax = Math.ceil(maxPrice / 100) * 100;
      setMaxProductPrice(roundedMax);
      setPriceRange({ min: 0, max: roundedMax });
    }
  }, [allProducts]);

  const categoriesWithCounts = useMemo(() => {
    const catMap = new Map();

    (categoriesData || []).forEach((cat) => {
      catMap.set(cat.CategoryID || cat.id, {
        ...cat,
        CategoryID: cat.CategoryID || cat.id,
        productCount: 0,
      });
    });

    catalogProducts.forEach((product) => {
      if (product.CategoryID) {
        const cat = catMap.get(product.CategoryID);
        if (cat) {
          cat.productCount = (cat.productCount || 0) + 1;
        }
      }
    });

    return Array.from(catMap.values()).filter((category) => category.productCount > 0);
  }, [categoriesData, catalogProducts]);

  const availableProductTypes = useMemo(() => {
    const types = new Set();
    catalogProducts.forEach((product) => {
      const type = getProductType(product);
      if (type) types.add(type);
    });
    return PRODUCT_TYPES.filter((t) => types.has(t.value));
  }, [catalogProducts]);

  const availableMeasurementTypes = useMemo(() => {
    const types = new Set();
    catalogProducts.forEach((product) => {
      const metadata = product.Metadata || {};
      const measType = metadata.measurementType;
      if (measType && measType !== "none") {
        types.add(measType);
      }
    });
    return Object.entries(MEASUREMENT_TYPES)
      .filter(([key]) => types.has(key))
      .map(([key, val]) => ({ value: key, ...val }));
  }, [catalogProducts]);

  const availableConditions = useMemo(() => {
    const conditions = new Set();
    catalogProducts.forEach((product) => {
      if (product.Condition) conditions.add(product.Condition);
    });
    return CONDITION_OPTIONS.filter((c) => conditions.has(c.value));
  }, [catalogProducts]);

  const availableLocations = useMemo(() => {
    const locations = new Set();
    catalogProducts.forEach((product) => {
      if (product.Location) locations.add(product.Location);
    });
    return SA_PROVINCES.filter((p) => locations.has(p));
  }, [catalogProducts]);

  const availableAnimalTypes = useMemo(() => {
    const types = new Set();
    catalogProducts.forEach((product) => {
      const metadata = product.Metadata || {};
      if (metadata.animalType) types.add(metadata.animalType);
    });
    return ANIMAL_TYPES.filter((t) => types.has(t));
  }, [catalogProducts]);

  const hasLivestockProducts = useMemo(() => {
    return catalogProducts.some(
      (p) =>
        getProductType(p) === "livestock",
    );
  }, [catalogProducts]);

  // ============================================
  // ACTIVE FILTER COUNT
  // ============================================
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedCategories.length > 0) count += selectedCategories.length;
    if (selectedProductTypes.length > 0) count += selectedProductTypes.length;
    if (priceRange.min > 0 || priceRange.max < maxProductPrice) count += 1;
    if (selectedMeasurementTypes.length > 0)
      count += selectedMeasurementTypes.length;
    if (selectedConditions.length > 0) count += selectedConditions.length;
    if (selectedLocations.length > 0) count += selectedLocations.length;
    if (selectedAnimalTypes.length > 0) count += selectedAnimalTypes.length;
    if (showInStockOnly) count += 1;
    if (showOnSaleOnly) count += 1;
    if (showNewArrivalsOnly) count += 1;
    if (minRating > 0) count += 1;
    return count;
  }, [
    selectedCategories,
    selectedProductTypes,
    priceRange,
    maxProductPrice,
    selectedMeasurementTypes,
    selectedConditions,
    selectedLocations,
    selectedAnimalTypes,
    showInStockOnly,
    showOnSaleOnly,
    showNewArrivalsOnly,
    minRating,
  ]);

  // ============================================
  // FILTER AND SORT PRODUCTS
  // ============================================
  const filteredProducts = useMemo(() => {
    let products = [...catalogProducts];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      products = products.filter(
        (p) =>
          p.Name?.toLowerCase().includes(query) ||
          p.Description?.toLowerCase().includes(query) ||
          p.SKU?.toLowerCase().includes(query) ||
          p.CategoryName?.toLowerCase().includes(query),
      );
    }

    if (selectedCategories.length > 0) {
      const getAllDescendantIds = (catId) => {
        const ids = [catId];
        const children = categoriesWithCounts.filter(
          (c) => (c.ParentID || c.ParentCategoryID) === catId,
        );
        children.forEach((child) => {
          ids.push(...getAllDescendantIds(child.CategoryID));
        });
        return ids;
      };

      const allSelectedIds = new Set();
      selectedCategories.forEach((catId) => {
        getAllDescendantIds(catId).forEach((id) => allSelectedIds.add(id));
      });

      products = products.filter((p) => allSelectedIds.has(p.CategoryID));
    }

    if (selectedProductTypes.length > 0) {
      products = products.filter((p) => {
        const type = getProductType(p);
        return selectedProductTypes.includes(type);
      });
    }

    products = products.filter((p) => {
      const price = p.SellingPrice || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });

    if (selectedMeasurementTypes.length > 0) {
      products = products.filter((p) => {
        const metadata = p.Metadata || {};
        return selectedMeasurementTypes.includes(metadata.measurementType);
      });
    }

    if (selectedConditions.length > 0) {
      products = products.filter((p) => {
        return selectedConditions.includes(p.Condition);
      });
    }

    if (selectedLocations.length > 0) {
      products = products.filter((p) => {
        return selectedLocations.includes(p.Location);
      });
    }

    if (selectedAnimalTypes.length > 0) {
      products = products.filter((p) => {
        const metadata = p.Metadata || {};
        return selectedAnimalTypes.includes(metadata.animalType);
      });
    }

    if (showInStockOnly) {
      products = products.filter((p) => (p.StockQuantity || 0) > 0);
    }

    if (showOnSaleOnly) {
      products = products.filter((p) => {
        const promo = getProductPromotion(p.ProductID, p.CategoryID);
        return !!promo;
      });
    }

    if (showNewArrivalsOnly) {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      products = products.filter((p) => {
        const createdAt = new Date(p.CreatedAt);
        return createdAt >= thirtyDaysAgo;
      });
    }

    if (minRating > 0) {
      products = products.filter((p) => (p.Rating || 0) >= minRating);
    }

    switch (sortBy) {
      case "price-low":
        products.sort((a, b) => (a.SellingPrice || 0) - (b.SellingPrice || 0));
        break;
      case "price-high":
        products.sort((a, b) => (b.SellingPrice || 0) - (a.SellingPrice || 0));
        break;
      case "newest":
        products.sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));
        break;
      case "popular":
        products.sort((a, b) => (b.Rating || 0) - (a.Rating || 0));
        break;
      case "discount":
        products.sort((a, b) => {
          const aPromo = getProductPromotion(a.ProductID, a.CategoryID);
          const bPromo = getProductPromotion(b.ProductID, b.CategoryID);
          const aDiscount = aPromo ? parseFloat(aPromo.DiscountValue) || 0 : 0;
          const bDiscount = bPromo ? parseFloat(bPromo.DiscountValue) || 0 : 0;
          return bDiscount - aDiscount;
        });
        break;
      case "name-asc":
        products.sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
        break;
      case "name-desc":
        products.sort((a, b) => (b.Name || "").localeCompare(a.Name || ""));
        break;
      default:
        break;
    }

    return products;
  }, [
    catalogProducts,
    searchQuery,
    selectedCategories,
    selectedProductTypes,
    priceRange,
    selectedMeasurementTypes,
    selectedConditions,
    selectedLocations,
    selectedAnimalTypes,
    showInStockOnly,
    showOnSaleOnly,
    showNewArrivalsOnly,
    minRating,
    sortBy,
    categoriesWithCounts,
    productPromotionMap,
  ]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleAddToCart = async (product, quantity = 1) => {
    if (product.StockQuantity === 0) {
      toast.error("Product is out of stock");
      return;
    }

    try {
      const productId = product.ProductID || product.id;
      const promotion = getProductPromotion(productId, product.CategoryID);

      let finalPrice = product.SellingPrice || product.Price || 0;
      let originalPrice = finalPrice;
      let discountApplied = 0;

      if (promotion) {
        const discountValue = parseFloat(promotion.DiscountValue) || 0;

        if (promotion.DiscountType === "Percentage") {
          finalPrice =
            Math.round(finalPrice * (1 - discountValue / 100) * 100) / 100;
        } else if (promotion.DiscountType === "FixedAmount") {
          finalPrice = Math.max(
            0,
            Math.round((finalPrice - discountValue) * 100) / 100,
          );
        }

        discountApplied = originalPrice - finalPrice;
      }

      await addToCart(productId, quantity, {
        name: product.Name,
        price: finalPrice,
        originalPrice: originalPrice,
        imageUrl: product.ImageUrl,
        sku: product.SKU,
        promotion: promotion,
        discountApplied: discountApplied,
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add to cart");
    }
  };

  const resetAllFilters = () => {
    setSearchQuery("");
    setSelectedCategories([]);
    setSelectedProductTypes([]);
    setPriceRange({ min: 0, max: maxProductPrice });
    setPriceRangeInput({ min: "", max: "" });
    setSelectedMeasurementTypes([]);
    setSelectedConditions([]);
    setSelectedLocations([]);
    setSelectedAnimalTypes([]);
    setShowInStockOnly(false);
    setShowOnSaleOnly(false);
    setShowNewArrivalsOnly(false);
    setMinRating(0);
    setSortBy("featured");
  };

  const changeCatalogMode = (mode) => {
    if (mode === catalogMode) return;
    resetAllFilters();
    setCatalogMode(mode);
  };

  const toggleArrayFilter = (setter, value) => {
    setter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handlePriceRangeApply = () => {
    const min = parseInt(priceRangeInput.min) || 0;
    const max = parseInt(priceRangeInput.max) || maxProductPrice;
    setPriceRange({
      min: Math.max(0, min),
      max: Math.min(maxProductPrice, max),
    });
  };

  const hasActiveFilters = activeFilterCount > 0 || searchQuery;

  // ============================================
  // CATEGORY HELPERS
  // ============================================

  const getCategoryIcon = (categoryName) => {
    const name = (categoryName || "").toLowerCase();
    if (name.includes("men") || name.includes("male")) return "👔";
    if (name.includes("women") || name.includes("female")) return "👗";
    if (name.includes("shoe") || name.includes("footwear")) return "👟";
    if (name.includes("accessorie")) return "🕶️";
    if (name.includes("bag")) return "👜";
    if (name.includes("watch")) return "⌚";
    if (name.includes("jewelry") || name.includes("jewellery")) return "💍";
    if (name.includes("electronics") || name.includes("gadget")) return "📱";
    if (name.includes("home") || name.includes("living")) return "🏠";
    if (name.includes("sports") || name.includes("fitness")) return "⚽";
    if (name.includes("beauty") || name.includes("cosmetic")) return "💄";
    if (name.includes("kids") || name.includes("children")) return "🧸";
    if (name.includes("baby")) return "👶";
    if (name.includes("grocery") || name.includes("food")) return "🛒";
    if (name.includes("health") || name.includes("wellness")) return "💊";
    if (name.includes("pet")) return "🐾";
    if (name.includes("automotive") || name.includes("car")) return "🚗";
    if (name.includes("garden") || name.includes("plant")) return "🌱";
    if (name.includes("hardware") || name.includes("tool")) return "🔧";
    if (name.includes("gift")) return "🎁";
    if (name.includes("craft")) return "🎨";
    if (name.includes("musical") || name.includes("instrument")) return "🎸";
    if (name.includes("livestock") || name.includes("animal")) return "🐄";
    return "📦";
  };

  const categoryList = useMemo(() => {
    const visibleCategoryIds = new Set(
      categoriesWithCounts.map((category) => category.CategoryID),
    );
    const cats = (categoriesData || []).filter((category) =>
      visibleCategoryIds.has(category.CategoryID || category.id),
    );
    return cats.map((cat) => {
      let icon = cat.Icon || null;
      let iconType = cat.IconType || "emoji";
      let imageUrl = cat.ImageUrl || null;

      if (icon && typeof icon === "string") {
        const safeIcon = sanitizeImageUrl(icon);
        if (safeIcon) {
          if (
            safeIcon.startsWith("data:image/") ||
            safeIcon.startsWith("/uploads/") ||
            safeIcon.startsWith("http")
          ) {
            iconType = "image";
            imageUrl = safeIcon;
            icon = safeIcon;
          } else {
            iconType = "emoji";
          }
        }
      }

      if (imageUrl && !icon) {
        icon = sanitizeImageUrl(imageUrl) || imageUrl;
        iconType = "image";
      }

      if (!icon) {
        icon = getCategoryIcon(cat.Name || cat.name || "");
        iconType = "emoji";
      }

      return {
        id: cat.CategoryID || cat.id,
        name: cat.Name || cat.name || "Unnamed",
        icon,
        iconType,
        imageUrl,
      };
    });
  }, [categoriesData, categoriesWithCounts]);

  // Mobile category handlers
  const openMobileCategorySheet = () => {
    setShowMobileCategorySheet(true);
    document.body.style.overflow = "hidden";
  };

  const closeMobileCategorySheet = () => {
    setShowMobileCategorySheet(false);
    document.body.style.overflow = "";
  };

  const handleMobileCategorySelect = (categoryId) => {
    if (categoryId === "all") {
      setSelectedCategories([]);
      closeMobileCategorySheet();
      return;
    }

    setSelectedCategories([categoryId]);
    closeMobileCategorySheet();
  };

  // ============================================
  // LOADING / ERROR STATES
  // ============================================
  if (tenantLoading || isLoading || categoriesLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="shop-error">
        <div className="shop-error-content">
          <h2>Error Loading Products</h2>
          <p>{error.message}</p>
          <button onClick={() => refetch()}>Retry</button>
        </div>
      </div>
    );
  }

  const onSaleCount = productPromotionMap.size;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div
      className={`shop-page ${
        storefrontContent.productGrid.showFilters
          ? ""
          : "storefront-filters-hidden"
      }`}
      data-mobile-category-side={storefrontContent.mobileLayout.categoryTabSide}
      data-category-size={storefrontContent.categoryLayout.size}
      style={{
        "--shop-category-columns":
          [4, 6, 9].includes(Number(storefrontContent.categoryLayout.desktopColumns))
            ? Number(storefrontContent.categoryLayout.desktopColumns)
            : 9,
      }}
    >
      {/* MOBILE FLOATING CATEGORIES TAB */}
      <button
        className="mobile-category-tab"
        onClick={openMobileCategorySheet}
        aria-label="Browse categories"
      >
        <span className="category-tab-icon">
          <FiMenu size={20} />
        </span>
        <span className="category-tab-text">CATEGORIES</span>
      </button>

      <div className="container">
        <StorefrontCategoryIntro config={storefrontContent.categoryIntro} />

        {/* Shop Header */}
        <div className="shop-header">
          <div className="shop-header-left">
            <h1 className="shop-title">Our Collection</h1>
            {onSaleCount > 0 && (
              <span className="shop-sale-badge">
                <FiTag />
                {onSaleCount} items on sale
              </span>
            )}
          </div>
          <div className="shop-header-right">
            <span className="shop-product-count">
              {filteredProducts.length} {catalogMode === "bookings" ? "bookings" : "products"}
            </span>
          </div>
        </div>

        {/* Main Layout: Sidebar + Content */}
        <div className="shop-layout">
          {/* ============================================ */}
          {/* DESKTOP/TABLET FILTER SIDEBAR (ALWAYS VISIBLE) */}
          {/* ============================================ */}
          <aside className="shop-sidebar" aria-label="Product filters">
            <div className="shop-sidebar-header">
              <h2 className="sidebar-title">
                <FiSliders size={18} />
                Filters
                {activeFilterCount > 0 && (
                  <span className="active-filter-count">
                    {activeFilterCount}
                  </span>
                )}
              </h2>
              <div className="sidebar-header-actions">
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="sidebar-reset-btn"
                    onClick={resetAllFilters}
                    title="Reset all filters"
                  >
                    <FiRefreshCw size={14} />
                    Reset
                  </button>
                )}
              </div>
            </div>

            <div className="shop-sidebar-content">
              {/* CATEGORIES FILTER */}
              {categoriesWithCounts.length > 0 && (
                <FilterSection
                  title="Categories"
                  icon={FiFolder}
                  count={selectedCategories.length}
                >
                  <div className="filter-category-list">
                    <button
                      type="button"
                      className={`filter-category-all ${
                        selectedCategories.length === 0 ? "active" : ""
                      }`}
                      onClick={() => setSelectedCategories([])}
                    >
                      <span className="category-all-icon">🏠</span>
                      <span className="category-all-name">All Categories</span>
                      <span className="category-all-count">
                        {catalogProducts.length}
                      </span>
                    </button>
                    <CategoryTree
                      categories={categoriesWithCounts}
                      selectedCategories={selectedCategories}
                      onSelect={(catId) =>
                        toggleArrayFilter(setSelectedCategories, catId)
                      }
                    />
                  </div>
                </FilterSection>
              )}

              {/* PRICE RANGE FILTER */}
              <FilterSection title="Price Range" icon={FiDollarSign}>
                <div className="filter-price-range">
                  <div className="price-slider-container">
                    <input
                      type="range"
                      min={0}
                      max={maxProductPrice}
                      value={priceRange.max}
                      onChange={(e) =>
                        setPriceRange({
                          ...priceRange,
                          max: parseInt(e.target.value),
                        })
                      }
                      className="filter-price-slider"
                    />
                    <div className="price-slider-values">
                      <span>R{priceRange.min}</span>
                      <span>R{priceRange.max}</span>
                    </div>
                  </div>
                  <div className="price-input-row">
                    <div className="price-input-group">
                      <span className="price-currency">R</span>
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRangeInput.min}
                        onChange={(e) =>
                          setPriceRangeInput({
                            ...priceRangeInput,
                            min: e.target.value,
                          })
                        }
                        min="0"
                        className="price-range-input"
                      />
                    </div>
                    <span className="price-separator">—</span>
                    <div className="price-input-group">
                      <span className="price-currency">R</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRangeInput.max}
                        onChange={(e) =>
                          setPriceRangeInput({
                            ...priceRangeInput,
                            max: e.target.value,
                          })
                        }
                        min="0"
                        className="price-range-input"
                      />
                    </div>
                    <button
                      type="button"
                      className="price-apply-btn"
                      onClick={handlePriceRangeApply}
                    >
                      <FiCheck size={14} />
                    </button>
                  </div>
                  <div className="price-quick-ranges">
                    {[
                      { label: "Under R100", min: 0, max: 100 },
                      { label: "R100–R500", min: 100, max: 500 },
                      { label: "R500–R1000", min: 500, max: 1000 },
                      { label: "R1000+", min: 1000, max: maxProductPrice },
                    ].map((range) => (
                      <button
                        key={range.label}
                        type="button"
                        className={`price-quick-btn ${
                          priceRange.min === range.min &&
                          priceRange.max === range.max
                            ? "active"
                            : ""
                        }`}
                        onClick={() =>
                          setPriceRange({ min: range.min, max: range.max })
                        }
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>
              </FilterSection>

              {/* PRODUCT TYPE FILTER */}
              {availableProductTypes.length > 0 && (
                <FilterSection
                  title="Product Type"
                  icon={FiPackage}
                  count={selectedProductTypes.length}
                >
                  <div className="filter-checkbox-list">
                    {availableProductTypes.map((type) => {
                      const Icon = type.icon;
                      const isChecked = selectedProductTypes.includes(
                        type.value,
                      );
                      return (
                        <label
                          key={type.value}
                          className={`filter-checkbox-item ${
                            isChecked ? "checked" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() =>
                              toggleArrayFilter(
                                setSelectedProductTypes,
                                type.value,
                              )
                            }
                          />
                          <span className="filter-checkbox-custom">
                            {isChecked && <FiCheck size={12} />}
                          </span>
                          <Icon
                            size={14}
                            className="filter-checkbox-icon"
                            style={{ color: type.color }}
                          />
                          <span className="filter-checkbox-label">
                            {type.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </FilterSection>
              )}

              {/* MEASUREMENT TYPE FILTER */}
              {availableMeasurementTypes.length > 0 && (
                <FilterSection
                  title="Measurements"
                  icon={MdOutlineStraighten}
                  count={selectedMeasurementTypes.length}
                  defaultOpen={false}
                >
                  <div className="filter-checkbox-list">
                    {availableMeasurementTypes.map((meas) => {
                      const Icon = meas.icon;
                      const isChecked = selectedMeasurementTypes.includes(
                        meas.value,
                      );
                      return (
                        <label
                          key={meas.value}
                          className={`filter-checkbox-item ${
                            isChecked ? "checked" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() =>
                              toggleArrayFilter(
                                setSelectedMeasurementTypes,
                                meas.value,
                              )
                            }
                          />
                          <span className="filter-checkbox-custom">
                            {isChecked && <FiCheck size={12} />}
                          </span>
                          <Icon size={14} className="filter-checkbox-icon" />
                          <span className="filter-checkbox-label">
                            {meas.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </FilterSection>
              )}

              {/* ANIMAL TYPE FILTER (Livestock only) */}
              {hasLivestockProducts && availableAnimalTypes.length > 0 && (
                <FilterSection
                  title="Animal Type"
                  icon={FaPiggyBank}
                  count={selectedAnimalTypes.length}
                  defaultOpen={false}
                >
                  <div className="filter-checkbox-list">
                    {availableAnimalTypes.map((animalType) => {
                      const isChecked =
                        selectedAnimalTypes.includes(animalType);
                      return (
                        <label
                          key={animalType}
                          className={`filter-checkbox-item ${
                            isChecked ? "checked" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() =>
                              toggleArrayFilter(
                                setSelectedAnimalTypes,
                                animalType,
                              )
                            }
                          />
                          <span className="filter-checkbox-custom">
                            {isChecked && <FiCheck size={12} />}
                          </span>
                          <span className="filter-checkbox-label">
                            {animalType}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </FilterSection>
              )}

              {/* CONDITION FILTER */}
              {availableConditions.length > 0 && (
                <FilterSection
                  title="Condition"
                  icon={FiCheckCircle}
                  count={selectedConditions.length}
                  defaultOpen={false}
                >
                  <div className="filter-checkbox-list">
                    {availableConditions.map((cond) => {
                      const isChecked = selectedConditions.includes(cond.value);
                      return (
                        <label
                          key={cond.value}
                          className={`filter-checkbox-item ${
                            isChecked ? "checked" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() =>
                              toggleArrayFilter(
                                setSelectedConditions,
                                cond.value,
                              )
                            }
                          />
                          <span className="filter-checkbox-custom">
                            {isChecked && <FiCheck size={12} />}
                          </span>
                          <span className="filter-checkbox-label">
                            {cond.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </FilterSection>
              )}

              {/* LOCATION FILTER */}
              {availableLocations.length > 0 && (
                <FilterSection
                  title="Location"
                  icon={FiMapPin}
                  count={selectedLocations.length}
                  defaultOpen={false}
                >
                  <div className="filter-checkbox-list">
                    {availableLocations.map((location) => {
                      const isChecked = selectedLocations.includes(location);
                      return (
                        <label
                          key={location}
                          className={`filter-checkbox-item ${
                            isChecked ? "checked" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() =>
                              toggleArrayFilter(setSelectedLocations, location)
                            }
                          />
                          <span className="filter-checkbox-custom">
                            {isChecked && <FiCheck size={12} />}
                          </span>
                          <span className="filter-checkbox-label">
                            {location}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </FilterSection>
              )}

              {/* AVAILABILITY FILTER */}
              <FilterSection title="Availability" icon={FiShoppingBag}>
                <div className="filter-checkbox-list">
                  <label
                    className={`filter-checkbox-item ${
                      showInStockOnly ? "checked" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={showInStockOnly}
                      onChange={(e) => setShowInStockOnly(e.target.checked)}
                    />
                    <span className="filter-checkbox-custom">
                      {showInStockOnly && <FiCheck size={12} />}
                    </span>
                    <span className="filter-checkbox-label">In Stock</span>
                  </label>
                  <label
                    className={`filter-checkbox-item ${
                      showOnSaleOnly ? "checked" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={showOnSaleOnly}
                      onChange={(e) => setShowOnSaleOnly(e.target.checked)}
                    />
                    <span className="filter-checkbox-custom">
                      {showOnSaleOnly && <FiCheck size={12} />}
                    </span>
                    <span className="filter-checkbox-label">On Sale</span>
                  </label>
                  <label
                    className={`filter-checkbox-item ${
                      showNewArrivalsOnly ? "checked" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={showNewArrivalsOnly}
                      onChange={(e) => setShowNewArrivalsOnly(e.target.checked)}
                    />
                    <span className="filter-checkbox-custom">
                      {showNewArrivalsOnly && <FiCheck size={12} />}
                    </span>
                    <span className="filter-checkbox-label">New Arrivals</span>
                  </label>
                </div>
              </FilterSection>

              {/* RATING FILTER */}
              <FilterSection title="Rating" icon={FiStar} defaultOpen={false}>
                <div className="filter-rating-list">
                  {[4, 3, 2, 1].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      className={`filter-rating-btn ${
                        minRating === rating ? "active" : ""
                      }`}
                      onClick={() =>
                        setMinRating(minRating === rating ? 0 : rating)
                      }
                    >
                      <div className="rating-stars">
                        {[...Array(5)].map((_, i) => (
                          <FiStar
                            key={i}
                            size={14}
                            className={
                              i < rating ? "star-filled" : "star-empty"
                            }
                          />
                        ))}
                      </div>
                      <span className="rating-label">& Up</span>
                    </button>
                  ))}
                </div>
              </FilterSection>
            </div>

            {/* Sidebar Footer */}
            <div className="shop-sidebar-footer">
              <div className="sidebar-results-count">
                <strong>{filteredProducts.length}</strong> products found
              </div>
            </div>
          </aside>

          {/* ============================================ */}
          {/* MAIN CONTENT AREA */}
          {/* ============================================ */}
          <main className="shop-content">
            {/* Search Bar */}
            <div className="shop-search-section">
              <div className="shop-search-wrapper">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="shop-search-input"
                />
                {searchQuery && (
                  <button
                    className="search-clear"
                    onClick={() => setSearchQuery("")}
                  >
                    <FiX />
                  </button>
                )}
              </div>
            </div>

            {/* Filter Bar (sort/view + mobile filter button) */}
            <div className="shop-filter-bar">
              <div className="filter-bar-left">
                {/* Mobile Filter Button */}
                {storefrontContent.productGrid.showFilters && (
                  <button
                    className="filter-toggle-btn mobile-only"
                    onClick={() => setShowMobileFilterSheet(true)}
                  >
                    <FiFilter />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="filter-dot">{activeFilterCount}</span>
                    )}
                  </button>
                )}

                {/* Sort Dropdown */}
                <div className="sort-wrapper">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <FiChevronDown className="sort-arrow" />
                </div>
              </div>

              <div className="filter-bar-right">
                <button
                  className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                >
                  <FiGrid />
                </button>
                <button
                  className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                  aria-label="List view"
                >
                  <FiList />
                </button>
              </div>
            </div>

            {/* Category Cards Grid (Desktop/Tablet only) */}
            <div className="shop-catalog-mode-area">
              <CatalogModeTabs
                catalogMode={catalogMode}
                onChange={changeCatalogMode}
                productsCount={productsCount}
                bookingsCount={bookingsCount}
                bookingToggle={bookingToggle}
                primaryColor={storefrontPrimaryColor}
              />
            </div>

            {categoryList.length > 0 && (
              <div className="shop-categories-section">
                <div className="shop-categories-grid">
                  {categoryList.slice(0, 9).map((category) => {
                    const isImage =
                      category.iconType === "image" && category.icon;
                    const isSelected = selectedCategories.includes(category.id);

                    return (
                      <div
                        key={category.id}
                        className={`shop-category-card ${
                          isSelected ? "active" : ""
                        }`}
                        onClick={() => {
                          toggleArrayFilter(setSelectedCategories, category.id);
                        }}
                        title={
                          isSelected
                            ? "Click again to clear this category"
                            : category.name
                        }
                      >
                        <div className="shop-category-icon-wrapper">
                          {isImage ? (
                            <img
                              src={category.icon}
                              alt={category.name}
                              className="shop-category-image"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = PLACEHOLDER_IMAGE;
                              }}
                            />
                          ) : (
                            <span className="shop-category-icon">
                              {category.icon}
                            </span>
                          )}
                        </div>
                        <span className="shop-category-name">
                          {category.name}
                        </span>
                        {isSelected && (
                          <span
                            className="shop-category-selected-badge"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCategories((prev) =>
                                prev.filter((id) => id !== category.id),
                              );
                            }}
                            title="Clear category"
                          >
                            <FiX size={10} />
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Active Filters Tags */}
            {hasActiveFilters && (
              <div className="active-filters">
                {searchQuery && (
                  <span className="filter-tag">
                    Search: "{searchQuery}"
                    <button onClick={() => setSearchQuery("")}>
                      <FiX size={14} />
                    </button>
                  </span>
                )}

                {selectedCategories.map((catId) => {
                  const cat = categoriesWithCounts.find(
                    (c) => c.CategoryID === catId,
                  );
                  return (
                    <span key={catId} className="filter-tag">
                      {cat?.Name || "Category"}
                      <button
                        onClick={() =>
                          toggleArrayFilter(setSelectedCategories, catId)
                        }
                      >
                        <FiX size={14} />
                      </button>
                    </span>
                  );
                })}

                {selectedProductTypes.map((type) => {
                  const typeConfig = PRODUCT_TYPES.find(
                    (t) => t.value === type,
                  );
                  return (
                    <span key={type} className="filter-tag">
                      {typeConfig?.label || type}
                      <button
                        onClick={() =>
                          toggleArrayFilter(setSelectedProductTypes, type)
                        }
                      >
                        <FiX size={14} />
                      </button>
                    </span>
                  );
                })}

                {(priceRange.min > 0 || priceRange.max < maxProductPrice) && (
                  <span className="filter-tag">
                    R{priceRange.min} - R{priceRange.max}
                    <button
                      onClick={() =>
                        setPriceRange({ min: 0, max: maxProductPrice })
                      }
                    >
                      <FiX size={14} />
                    </button>
                  </span>
                )}

                {selectedMeasurementTypes.map((meas) => {
                  const measConfig = MEASUREMENT_TYPES[meas];
                  return (
                    <span key={meas} className="filter-tag">
                      {measConfig?.label || meas}
                      <button
                        onClick={() =>
                          toggleArrayFilter(setSelectedMeasurementTypes, meas)
                        }
                      >
                        <FiX size={14} />
                      </button>
                    </span>
                  );
                })}

                {selectedConditions.map((cond) => (
                  <span key={cond} className="filter-tag">
                    {cond}
                    <button
                      onClick={() =>
                        toggleArrayFilter(setSelectedConditions, cond)
                      }
                    >
                      <FiX size={14} />
                    </button>
                  </span>
                ))}

                {selectedLocations.map((loc) => (
                  <span key={loc} className="filter-tag">
                    <FiMapPin size={12} /> {loc}
                    <button
                      onClick={() =>
                        toggleArrayFilter(setSelectedLocations, loc)
                      }
                    >
                      <FiX size={14} />
                    </button>
                  </span>
                ))}

                {selectedAnimalTypes.map((animal) => (
                  <span key={animal} className="filter-tag">
                    {animal}
                    <button
                      onClick={() =>
                        toggleArrayFilter(setSelectedAnimalTypes, animal)
                      }
                    >
                      <FiX size={14} />
                    </button>
                  </span>
                ))}

                {showInStockOnly && (
                  <span className="filter-tag">
                    In Stock
                    <button onClick={() => setShowInStockOnly(false)}>
                      <FiX size={14} />
                    </button>
                  </span>
                )}

                {showOnSaleOnly && (
                  <span className="filter-tag">
                    On Sale
                    <button onClick={() => setShowOnSaleOnly(false)}>
                      <FiX size={14} />
                    </button>
                  </span>
                )}

                {showNewArrivalsOnly && (
                  <span className="filter-tag">
                    New Arrivals
                    <button onClick={() => setShowNewArrivalsOnly(false)}>
                      <FiX size={14} />
                    </button>
                  </span>
                )}

                {minRating > 0 && (
                  <span className="filter-tag">
                    {minRating}+ Stars
                    <button onClick={() => setMinRating(0)}>
                      <FiX size={14} />
                    </button>
                  </span>
                )}

                {activeFilterCount > 1 && (
                  <button
                    className="filter-tag clear-all-tag"
                    onClick={resetAllFilters}
                  >
                    Clear All
                  </button>
                )}
              </div>
            )}

            {/* Product Grid */}
            <div className="shop-product-grid-container">
              {catalogMode === "bookings" ? (
                bookingsError ? (
                  <div className="shop-error">
                    <div className="shop-error-content">
                      <h2>Error Loading Booking Services</h2>
                      <p>{bookingsError.message}</p>
                      <button onClick={() => refetchBookings()}>Retry</button>
                    </div>
                  </div>
                ) : (
                  <BookingGrid
                    bookings={filteredProducts}
                    loading={bookingsLoading}
                    onBookNow={(booking) =>
                      navigate(`/booking/${booking.ProductID}`)
                    }
                    primaryColor={storefrontPrimaryColor}
                    cardStyle={bookingToggle.bookingCardStyle}
                  />
                )
              ) : (
                <ProductGrid
                  products={filteredProducts}
                  loading={isLoading}
                  onAddToCart={handleAddToCart}
                  promotions={promotionsData?.promotions || []}
                  getProductPromotion={getProductPromotion}
                  viewMode={viewMode}
                  gridConfig={storefrontContent.productGrid}
                  interstitials={inlineBanners.map((slide) => ({
                    id: slide.id,
                    afterProducts: slide.afterProducts,
                    content: <StorefrontInlineBanner slide={slide} />,
                  }))}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {/* ============================================ */}
      {/* MOBILE FILTER SHEET */}
      {/* ============================================ */}
      {showMobileFilterSheet && (
        <>
          <div
            className="mobile-filter-overlay"
            onClick={() => setShowMobileFilterSheet(false)}
          />
          <div className="mobile-filter-sheet">
            <div className="mobile-filter-sheet-handle" />
            <div className="mobile-filter-sheet-header">
              <span className="mobile-filter-sheet-title">
                <FiSliders size={18} /> Filters
              </span>
              <button
                className="mobile-filter-sheet-close"
                onClick={() => setShowMobileFilterSheet(false)}
                aria-label="Close"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="mobile-filter-sheet-body">
              {categoriesWithCounts.length > 0 && (
                <FilterSection
                  title="Categories"
                  icon={FiFolder}
                  count={selectedCategories.length}
                >
                  <div className="filter-category-list">
                    <button
                      type="button"
                      className={`filter-category-all ${
                        selectedCategories.length === 0 ? "active" : ""
                      }`}
                      onClick={() => setSelectedCategories([])}
                    >
                      <span className="category-all-icon">🏠</span>
                      <span className="category-all-name">All Categories</span>
                    </button>
                    <CategoryTree
                      categories={categoriesWithCounts}
                      selectedCategories={selectedCategories}
                      onSelect={(catId) =>
                        toggleArrayFilter(setSelectedCategories, catId)
                      }
                    />
                  </div>
                </FilterSection>
              )}

              <FilterSection title="Price Range" icon={FiDollarSign}>
                <div className="filter-price-range">
                  <div className="price-slider-container">
                    <input
                      type="range"
                      min={0}
                      max={maxProductPrice}
                      value={priceRange.max}
                      onChange={(e) =>
                        setPriceRange({
                          ...priceRange,
                          max: parseInt(e.target.value),
                        })
                      }
                      className="filter-price-slider"
                    />
                    <div className="price-slider-values">
                      <span>R{priceRange.min}</span>
                      <span>R{priceRange.max}</span>
                    </div>
                  </div>
                </div>
              </FilterSection>

              {availableProductTypes.length > 0 && (
                <FilterSection
                  title="Product Type"
                  icon={FiPackage}
                  count={selectedProductTypes.length}
                >
                  <div className="filter-checkbox-list">
                    {availableProductTypes.map((type) => {
                      const Icon = type.icon;
                      const isChecked = selectedProductTypes.includes(
                        type.value,
                      );
                      return (
                        <label
                          key={type.value}
                          className={`filter-checkbox-item ${
                            isChecked ? "checked" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() =>
                              toggleArrayFilter(
                                setSelectedProductTypes,
                                type.value,
                              )
                            }
                          />
                          <span className="filter-checkbox-custom">
                            {isChecked && <FiCheck size={12} />}
                          </span>
                          <Icon size={14} style={{ color: type.color }} />
                          <span className="filter-checkbox-label">
                            {type.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </FilterSection>
              )}

              <FilterSection title="Availability" icon={FiShoppingBag}>
                <div className="filter-checkbox-list">
                  <label
                    className={`filter-checkbox-item ${
                      showInStockOnly ? "checked" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={showInStockOnly}
                      onChange={(e) => setShowInStockOnly(e.target.checked)}
                    />
                    <span className="filter-checkbox-custom">
                      {showInStockOnly && <FiCheck size={12} />}
                    </span>
                    <span className="filter-checkbox-label">In Stock</span>
                  </label>
                  <label
                    className={`filter-checkbox-item ${
                      showOnSaleOnly ? "checked" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={showOnSaleOnly}
                      onChange={(e) => setShowOnSaleOnly(e.target.checked)}
                    />
                    <span className="filter-checkbox-custom">
                      {showOnSaleOnly && <FiCheck size={12} />}
                    </span>
                    <span className="filter-checkbox-label">On Sale</span>
                  </label>
                  <label
                    className={`filter-checkbox-item ${
                      showNewArrivalsOnly ? "checked" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={showNewArrivalsOnly}
                      onChange={(e) => setShowNewArrivalsOnly(e.target.checked)}
                    />
                    <span className="filter-checkbox-custom">
                      {showNewArrivalsOnly && <FiCheck size={12} />}
                    </span>
                    <span className="filter-checkbox-label">New Arrivals</span>
                  </label>
                </div>
              </FilterSection>
            </div>
            <div className="mobile-filter-sheet-footer">
              <button
                className="mobile-filter-reset-btn"
                onClick={resetAllFilters}
              >
                Reset All
              </button>
              <button
                className="mobile-filter-apply-btn"
                onClick={() => setShowMobileFilterSheet(false)}
              >
                Show {filteredProducts.length} Products
              </button>
            </div>
          </div>
        </>
      )}

      {/* ============================================ */}
      {/* MOBILE CATEGORY SHEET */}
      {/* ============================================ */}
      {showMobileCategorySheet && (
        <>
          <div
            className="mobile-category-overlay"
            onClick={closeMobileCategorySheet}
          />
          <div className="mobile-category-sheet">
            <div className="mobile-category-sheet-handle" />
            <div className="mobile-category-sheet-header">
              <span className="mobile-category-sheet-title">
                Browse Categories
              </span>
              <button
                className="mobile-category-sheet-close"
                onClick={closeMobileCategorySheet}
                aria-label="Close"
              >
                <FiX size={18} />
              </button>
            </div>
            <div className="mobile-category-sheet-body">
              <div className="mobile-category-grid">
                <button
                  className={`mobile-category-chip ${
                    selectedCategories.length === 0 ? "active" : ""
                  }`}
                  onClick={() => handleMobileCategorySelect("all")}
                >
                  <span className="mobile-category-chip-icon">🏠</span>
                  <span className="mobile-category-chip-name">All</span>
                </button>

                {categoryList.map((cat) => {
                  const isImage = cat.iconType === "image" && cat.icon;
                  const isSelected = selectedCategories.includes(cat.id);

                  return (
                    <button
                      key={cat.id}
                      className={`mobile-category-chip ${
                        isSelected ? "active" : ""
                      }`}
                      onClick={() => handleMobileCategorySelect(cat.id)}
                    >
                      {isImage ? (
                        <img
                          src={cat.icon}
                          alt={cat.name}
                          className="mobile-category-chip-img"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = PLACEHOLDER_IMAGE;
                          }}
                        />
                      ) : (
                        <span className="mobile-category-chip-icon">
                          {cat.icon}
                        </span>
                      )}
                      <span className="mobile-category-chip-name">
                        {cat.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Shop;
